
import {
  DOMAIN_FEATURE_MAP,
  DOMAIN_VOICE_CHAT_CHILD_FEATURES,
  FEATURE_AUTO_ENABLE_CHILDREN,
  FEATURE_CHILDREN_MAP,
  FEATURE_METADATA_MAP,
} from '@/constants';
import { BotDomain, SupportedFeature } from 'ignite360-core';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component({
  name: 'bot-features-checkbox-group',
})
export default class BotFeaturesCheckboxGroup extends Vue {
  @Prop({ required: false, type: String })
  readonly domain?: BotDomain;

  /** When set, overrides domain-based feature list from DOMAIN_FEATURE_MAP. */
  @Prop({ required: false, type: Array })
  readonly featureList?: SupportedFeature[];

  @Prop({ required: true, type: Set })
  readonly value!: Set<SupportedFeature>;

  @Prop({ required: false, type: Boolean, default: false })
  readonly disabled!: boolean;

  get features(): SupportedFeature[] {
    if (this.featureList) {
      return this.featureList;
    }
    if (!this.domain) {
      return [];
    }
    return DOMAIN_FEATURE_MAP[this.domain];
  }

  get featureMetadataMap() {
    return FEATURE_METADATA_MAP;
  }

  childrenFor(feature: SupportedFeature): SupportedFeature[] {
    const children = FEATURE_CHILDREN_MAP[feature] || [];
    if (!this.domain || feature !== SupportedFeature.VoiceAndChat) {
      return children;
    }
    const allowed = DOMAIN_VOICE_CHAT_CHILD_FEATURES[this.domain] || [];
    return children.filter((child) => allowed.includes(child));
  }

  /** Children that always follow their parent and cannot be toggled off independently. */
  isAutoEnabledChild(child: SupportedFeature): boolean {
    return FEATURE_AUTO_ENABLE_CHILDREN.some((parent) =>
      (FEATURE_CHILDREN_MAP[parent] || []).includes(child),
    );
  }

  isAutoEnableParent(feature: SupportedFeature): boolean {
    return FEATURE_AUTO_ENABLE_CHILDREN.includes(feature);
  }

  toggleFeature(feature: SupportedFeature) {
    const parentOfAutoChild = FEATURE_AUTO_ENABLE_CHILDREN.find((parent) =>
      (FEATURE_CHILDREN_MAP[parent] || []).includes(feature),
    );
    if (parentOfAutoChild && this.value.has(parentOfAutoChild)) {
      // Auto-tied children can only be changed by toggling their parent.
      return;
    }

    const valueCopy = new Set(this.value);
    if (valueCopy.has(feature)) {
      valueCopy.delete(feature);
      for (const child of this.childrenFor(feature)) {
        valueCopy.delete(child);
      }
    } else {
      valueCopy.add(feature);
      if (this.isAutoEnableParent(feature)) {
        for (const child of this.childrenFor(feature)) {
          valueCopy.add(child);
        }
      }
    }
    this.$emit('input', valueCopy);
  }
}
